import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Droplets, Sparkles, SprayCan, Sun } from "lucide-react";

const FEATURES = [
  {
    icon: Droplets,
    title: "Snow-foam wash.",
    body: "pH-neutral foam lifts grit before any mitt ever touches your paint.",
  },
  {
    icon: Sparkles,
    title: "Two-bucket method.",
    body: "Grit-guard buckets, paint-safe mitts, straight-line motions only.",
  },
  {
    icon: SprayCan,
    title: "Interior reset.",
    body: "Every crevice vacuumed. Leather conditioned. Glass streak-free.",
  },
  {
    icon: Sun,
    title: "Mobile · summer only.",
    body: "June 18 to September 6. 9 AM to 10 PM. Niskayuna NY.",
  },
];

export default function Features() {
  return (
    <section
      id="how"
      className="relative section"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl mb-16 md:mb-24"
        >
          <p className="overline mb-6 text-gold-bright">What's included</p>
          <h2 className="section-heading">
            Every detail,{" "}
            <br className="hidden sm:block" />{" "}
            <em
              className="italic"
              style={{
                fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif",
                background: "var(--gold-gradient)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: "gold-shimmer 6s linear infinite",
              }}
            >
              actually detailed.
            </em>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {FEATURES.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   FeatureCard — separate glass element.
   On hover: lifts, follows the mouse, rotates
   as if being picked up. On leave: springs back.
───────────────────────────────────────────── */
function FeatureCard({ feature, index }) {
  const { icon: Icon, title, body } = feature;
  const cardRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [glare, setGlare] = useState({ x: 50, y: 50, opacity: 0 });

  const handleMove = (e) => {
    const node = cardRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    // 8° max tilt; pulling the card toward the cursor
    const rotY = ((px - cx) / cx) * 7;
    const rotX = -((py - cy) / cy) * 7;
    setTilt({ x: rotX, y: rotY });
    setGlare({
      x: (px / rect.width) * 100,
      y: (py / rect.height) * 100,
      opacity: 1,
    });
  };

  const handleEnter = () => setHovered(true);
  const handleLeave = () => {
    setHovered(false);
    setTilt({ x: 0, y: 0 });
    setGlare({ x: 50, y: 50, opacity: 0 });
  };

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMove}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        opacity: { duration: 0.8, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] },
        y:       { duration: 0.8, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] },
      }}
      style={{
        perspective: 1000,
        transformStyle: "preserve-3d",
      }}
      data-testid={`feature-card-${index}`}
    >
      <motion.div
        animate={{
          rotateX: tilt.x,
          rotateY: tilt.y,
          y: hovered ? -10 : 0,
          scale: hovered ? 1.025 : 1,
        }}
        transition={{ type: "spring", stiffness: 260, damping: 22, mass: 0.6 }}
        style={{
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
        className="glass-card relative h-full"
      >
        {/* Cursor-following gold glare */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            pointerEvents: "none",
            background: `radial-gradient(360px circle at ${glare.x}% ${glare.y}%, rgba(170,170,170,0.20), transparent 60%)`,
            opacity: glare.opacity,
            transition: "opacity 0.3s ease",
          }}
        />

        {/* Subtle gold edge-glow that intensifies on hover */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            pointerEvents: "none",
            boxShadow: hovered
              ? "0 0 0 1px rgba(138,138,138,0.18), 0 28px 70px rgba(0,0,0,0.55), 0 0 60px rgba(138,138,138,0.10)"
              : "0 0 0 1px rgba(245,245,247,0.04), 0 18px 48px rgba(0,0,0,0.45)",
            transition: "box-shadow 0.45s cubic-bezier(0.16,1,0.3,1)",
          }}
        />

        <div
          className="relative h-full p-7 md:p-8 flex flex-col gap-5"
          style={{ transformStyle: "preserve-3d" }}
        >
          {/* Icon — slightly raised in 3D so it "lifts" first */}
          <motion.div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{
              background: "rgba(138,138,138,0.10)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 20px rgba(138,138,138,0.12)",
              transform: hovered ? "translateZ(28px)" : "translateZ(0)",
              transition: "transform 0.45s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            <Icon
              size={20}
              style={{ color: "#AAAAAA", strokeWidth: 1.5 }}
            />
          </motion.div>

          {/* Title */}
          <h3
            className="font-semibold tracking-[-0.02em] text-foreground"
            style={{
              fontSize: "clamp(1.1rem, 1.7vw, 1.35rem)",
              lineHeight: 1.15,
              transform: hovered ? "translateZ(18px)" : "translateZ(0)",
              transition: "transform 0.45s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            {title}
          </h3>

          {/* Body — light grey, but less than white */}
          <p
            className="text-[14px] leading-relaxed m-0"
            style={{
              color: "#c5c5cd",
              transform: hovered ? "translateZ(8px)" : "translateZ(0)",
              transition: "transform 0.45s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            {body}
          </p>

          {/* Learn more — gold, slides in on hover */}
          <div
            className="mt-auto pt-2 d-flex align-items-center"
            style={{
              gap: 6,
              color: "#AAAAAA",
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: "0.05em",
              opacity: hovered ? 1 : 0.7,
              transform: hovered
                ? "translateZ(20px) translateX(4px)"
                : "translateZ(0) translateX(0)",
              transition:
                "transform 0.45s cubic-bezier(0.16,1,0.3,1), opacity 0.3s ease",
            }}
          >
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
